<i18n>
{
  "en": {
    "zooming": "Zooming can be done with the mouse wheel",
    "titleChart": "Dynamics of resources and print volumes",
    "total": "total",
    "period": "by period",
    "averageDaily": "average daily",
    "availableDataPrintChart": "No data",
    "periodType": "Period",
    "from": "From",
    "to": "To"
  },
  "ru": {
    "zooming": "Для изменения масштаба диаграммы можно использовать колесо мыши",
    "titleChart": "Динамика остатков ресурсов и объемов печати",
    "total": "всего",
    "period": "за период",
    "averageDaily": "среднесуточно",
    "availableDataPrintChart": "Нет данных",
    "periodType": "Период",
    "from": "Начало",
    "to": "Окончание"
  }
}
</i18n>

<template>
    <div v-if="deviceDays">
        <div class="card-device">
            <div class="resource-counter">
                <div class="detail">
                    <DxSelectBox
                        class="dx-field-value"
                        :data-source="locations"
                        key="id"
                        :searchEnabled="true"
                        displayExpr="name"
                        valueExpr="id"
                        :showCancelButton="false"
                        labelMode="static"
                        :label="$t('device.information.location')"
                        :readOnly="!canEditDevice"
                        :dropDownOptions="{hideOnOutsideClick: true, showTitle: false}"
                        v-model="deviceDays.device.location.id"
                    />

                    <DxTextBox
                        class="dx-field-value"
                        labelMode="static"
                        :label="$t('device.information.inventoryNumber')"
                        :readOnly="!canEditDevice"
                        v-model="deviceDays.device.inventoryNumber"
                    />
                </div>

                <fieldset class="grid">
                    <legend>{{$t('device.information.resources')}}</legend>

                    <DxDataGrid
                        :data-source="resources"
                        :showColumnHeaders="false"
                        :showColumnLines="false"
                    >
                        <DxColumn
                            data-field="icon"
                            css-class="toner-icon"
                            :width="30"
                            cell-template="color-toner-cell"
                        />

                        <DxColumn
                            data-field="name"
                            :width="180"
                        />

                        <DxColumn
                            data-field="percentLevel"
                            cell-template="progress-cell"
                        />

                        <DxColumn
                            data-field="level"
                            alignment="right"
                            :width="50"
                        />

                        <template #color-toner-cell="{ data }">
                            <div class="fill" :style="getFillStyle(data.value)"></div>
                        </template>

                        <template #progress-cell="{ data }">
                            <div class="progress-cell">
                                <DxProgressBar
                                    :class="getClassProgress(data.value)"
                                    :min="0"
                                    :max="100"
                                    :value="data.value"
                                    :showStatus="false"
                                />
                            </div>
                        </template>
                    </DxDataGrid>
                </fieldset>

                <fieldset class="grid">
                    <legend>{{$t('device.information.counters')}}</legend>

                    <div class="heading">
                        <span class="caption"></span>
                        <div class="datagrid">
                            <span class="headers" :style="{width: '80px'}">{{$t('total')}}</span>
                            <span class="headers" :style="{width: '80px'}">{{$t('period')}}</span>
                            <span class="headers" :style="{width: '120px'}">{{$t('averageDaily')}}</span>
                        </div>
                    </div>

                    <DxDataGrid
                        :data-source="counters"
                        :showColumnHeaders="false"
                        :showColumnLines="false"
                    >
                        <DxColumn data-field="name" data-type="string" />

                        <DxColumn data-field="total" data-type="number" :width="80" />

                        <DxColumn data-field="period" data-type="number" :width="80" />

                        <DxColumn data-field="averageDaily" data-type="number" :width="120" />
                    </DxDataGrid>
                </fieldset>
            </div>

            <div
                v-resize:debounce="onResize"
                class="print-volumes"
            >
                <div class="period-type">
                    <DxLookup
                        :data-source="periodTypeDict"
                        key="code"
                        display-expr="name"
                        value-expr="code"
                        v-model="dataFilter.periodType"
                        :showCancelButton="false"
                        labelMode="static"
                        :label="$t('periodType')"
                        width="180px"
                        :disabled="loadChart"
                        :dropDownOptions="{hideOnOutsideClick: true, showTitle: false}"
                    />

                    <DxDateBox
                        v-model="dataFilter.from"
                        :label="$t('from')"
                        displayFormat="dd.MM.yyyy"
                        dateSerializationFormat="yyyy-MM-ddTHH:mm:ss"
                        :max="dataFilter.to || now"
                        width="120px"
                        :disabled="loadChart"
                        @value-changed="changeFrom"
                    />

                    <DxDateBox
                        v-model="dataFilter.to"
                        :label="$t('to')"
                        displayFormat="dd.MM.yyyy"
                        dateSerializationFormat="yyyy-MM-ddTHH:mm:ss"
                        :min="dataFilter.from || null"
                        :max="now"
                        width="120px"
                        :disabled="loadChart"
                        @value-changed="changeTo"
                    />
                </div>

                <fieldset class="device-print">
                    <legend>{{$t('titleChart')}}</legend>

                    <DxChart
                        id="chart"
                        :data-source="devices"
                        :size="{ height, width }"
                        default-pane="bottomPane"
                    >
                        <DxCommonSeriesSettings argument-field="factDate" type="stackedbar">
                            <DxPoint hover-mode="allArgumentPoints" :visible="true" :size="d && d.length === 1 ? 12 : 1" />
                        </DxCommonSeriesSettings>

                        <DxArgumentAxis discreteAxisDivisionMode="crossLabels" argumentType="string">
                            <DxLabel :customize-text="customizeLabelText"/>
                        </DxArgumentAxis>

                        <DxSeries v-for="part in p" :key="part.id"
                            pane="topPane"
                            type="spline"
                            :width="3"
                            :value-field="`${part.partCode}`"
                            :name="part.partName"
                            :color="color(part.partCode)"
                        />

                        <DxSeries
                            value-field="dayPrintedBwPageCount"
                            :name="$t('device.detail.bw')"
                            color="#9a9a9a"
                        />

                        <DxSeries v-if="dim02Code !== 'BW'"
                            value-field="dayPrintedColorPageCount"
                            :name="$t('device.detail.color')"
                            color="#ffb481"
                        />

                        <DxPane name="topPane"/>
                        <DxPane name="bottomPane"/>

                        <DxCommonAxisSettings aggregatedPointsPosition="crooTicks" />

                        <DxValueAxis pane="bottomPane" :visual-range="emptyRangeLabel"
                            :auto-breaks-enabled="true" :max-auto-break-count="1"
                        >
                            <DxGrid :visible="!emptyPrintChart"/>
                        </DxValueAxis>

                        <DxValueAxis pane="topPane" value-type="numeric"
                            :visual-range="{ startValue: 0, endValue: 100 }"
                            :whole-range="{ startValue: 0, endValue: 100 }"
                            :value-margins-enabled="false"
                        >
                            <DxGrid :visible="!emptyResourcesChart"/>
                        </DxValueAxis>

                        <DxScrollBar :visible="!emptyResourcesChart || !emptyPrintChart" :offset="16" :width="8" />
                        <DxZoomAndPan argument-axis="both" />
                        <DxLegend :visible="false" />

                        <dx-tooltip :shared="true" :enabled="true" content-template="tmpl1" />
                        <template #tmpl1="{ data }">
                            <div class="my-legend">
                                <div class="my-legend-title">{{ formatDateLegend(data.point.data.factDate) }}</div>
                                <table>
                                    <tr v-for="rec in p" v-bind:key="rec.id">
                                        <td v-if="data.point.data[`${rec.partCode}${rec.partId}`] != null">
                                            <div class="my-legend-serie" :style="`--serie-color: ${color(rec.partCode)}`">{{ `${rec.partCode} (${rec.partName}${rec.partSerialNumber? "(" + rec.partSerialNumber + ")": ""})` }}</div>
                                        </td>
                                        <td v-if="data.point.data[`${rec.partCode}${rec.partId}`] != null">
                                            <div class="my-legend-value">{{ data.point.data[`${rec.partCode}${rec.partId}`] }}</div>
                                        </td>
                                    </tr>

                                    <tr :class="{'print-volumes-bw' : !emptyResourcesChart}">
                                        <td>
                                            <div class="my-legend-serie" :style="`--serie-color: ${color($t('device.detail.bw'))}`">{{ $t('device.detail.bw') }}</div>
                                        </td>
                                        <td>
                                            <div class="my-legend-value">{{ data.point.data.dayPrintedBwPageCount }}</div>
                                        </td>
                                    </tr>

                                    <tr v-if="dim02Code !== 'BW'">
                                        <td>
                                            <div class="my-legend-serie" :style="`--serie-color: ${color($t('device.detail.color'))}`">{{ $t('device.detail.color') }}</div>
                                        </td>
                                        <td>
                                            <div class="my-legend-value">{{ data.point.data.dayPrintedColorPageCount }}</div>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>
                    </DxChart>

                    <Tooltip
                        :hide-on-outside-click="false"
                        target=".dxc-scroll-bar"
                        show-event="mouseenter"
                        hide-event="mouseleave"
                    >{{ $t('zooming') }}</Tooltip>

                    <div class="emptyChart emptyResourcesChart" v-if="emptyResourcesChart" :style="{height: (height / 2)  + 'px', width: (width - 25) + 'px'}">
                        <span class="text">{{ $t('availableDataPrintChart') }}</span>
                    </div>

                    <div class="emptyChart emptyPrintChart" v-if="emptyPrintChart" :style="{height: (height / 2)  + 'px', width: (width - 25) + 'px'}">
                        <span class="text">{{ $t('availableDataPrintChart') }}</span>
                    </div>

                    <dx-load-panel :visible="loadChart" :position="{ of: '.device-print' }" />
                </fieldset>

                <div class="comment">
                    <DxTextArea
                        class="dx-field-value"
                        :minHeight="55"
                        width="100%"
                        labelMode="static"
                        :label="$t('device.information.comment')"
                        :readOnly="!canEditDevice"
                        v-model="deviceDays.device.comment"
                    />
                </div>

                <div :class="classBtnActions()">
                    <aims2-button v-if="canEditDevice"
                        icon="save"
                        type="default"
                        :text="$t('general.save')"
                        :hint="$t('device.detail.actions.saveDevice')"
                        :busy="saving"
                        :disabled="saveDisabled"
                        @click="onSave"
                    />

                    <aims2-button v-if="canEditDevice"
                        icon="trash"
                        type="danger"
                        :text="$t('general.delete')"
                        :disabled="!deviceDays || !deviceDays.device.id"
                        :busy="deleting"
                        @click="onDelete"
                    />

                    <active-button v-if="isDownloadWalkFile"
                        icon="download"
                        type="normal"
                        :text="$t('device.detail.actions.downloadWalkFile')"
                        :disabled="disableDownloadWalkFileButton"
                        @click="onDownloadWalkFile"
                    />

                    <active-button v-if="isUpdateWalkFile"
                        type="normal"
                        :text="$t('device.detail.actions.updateWalkFile')"
                        :disabled="isNotAvailable"
                        @click="onUpdateWalkFile"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import resize from 'vue-resize-directive';

import DxSelectBox from 'devextreme-vue/select-box';
import DxLookup from 'devextreme-vue/lookup';
import DxDateBox from 'devextreme-vue/date-box';
import DxTextBox from 'devextreme-vue/text-box';
import DxTextArea from 'devextreme-vue/text-area';
import { DxProgressBar } from 'devextreme-vue/progress-bar';
import DxDataGrid, { DxColumn } from "devextreme-vue/data-grid";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import {
  DxChart, DxSeries, DxCommonSeriesSettings, DxLegend, DxTooltip, DxLabel, DxArgumentAxis,
  DxPane, DxValueAxis, DxGrid, DxPoint, DxCommonAxisSettings, DxScrollBar, DxZoomAndPan
} from 'devextreme-vue/chart';
import { DxTooltip as Tooltip } from 'devextreme-vue/tooltip';

import { notifier } from '@/shared/lib/notifier/index';

import { getFillStyle, getClassProgress, color } from '@/services/cell-consumables.js';
import { get } from "../../services/base";
import { AclHandler } from "@/services/acl-handler";
import { download, updateWalkFile, putDevice, deleteDevice, findDeviceDays, findDeviceResources } from "@/services/api";

import ActiveButton from '@/components/active-button.vue';
import aims2Button from '@/components/active-button.vue';
import showConfirmDialog from '@/components/confirm-dialog';
import { deviceResourcesTypes } from './entities/deviceResourcesTypes';

import { DataBacked } from "@/mixins/dataBackedMixin";
import { PeriodType } from "@/mixins/periodTypeMixin";

import { findIndex, sortBy } from 'lodash';
import moment from "moment";

const PAGE_WHITESPACE = 470;

export default {
    name: 'DeviceInformation',
    props: ['deviceDays', 'id', 'locations'],
    mixins: [DataBacked, PeriodType],
    components: {
        DxSelectBox, DxLookup, DxTextBox, DxTextArea, DxDataGrid, DxColumn, DxProgressBar, DxDateBox, DxChart, DxSeries,
        DxCommonSeriesSettings, DxLegend, DxTooltip, ActiveButton, aims2Button, DxLoadPanel, DxPane, DxValueAxis, DxGrid,
        DxPoint, DxCommonAxisSettings, DxScrollBar, DxZoomAndPan, Tooltip, DxLabel, DxArgumentAxis
    },
    data() {
        return {
            isCloudMode: this.$store.getters.isCloudMode,
            now: moment(),
            saving: false,
            saveDisabled: false,
            deleting: false,
            disableDownloadWalkFileButton: false,
            resources: [],
            devices: null,
            perPeriodTypesCounters: {
                dayPrintedBwPageCount: 0,
                dayPrintedColorPageCount: 0,
                dayPrintedPageA4Count: 0,
                dayPrintedBwPageA4Count: 0,
                dayPrintedColorPageA4Count: 0,
                dayPrintedPageA3Count: 0,
                dayPrintedBwPageA3Count: 0,
                dayPrintedColorPageA3Count: 0,
                dayPrintedBwPageEquivalentCount: 0,
                dayPrintedColorPageEquivalentCount: 0,
                dayCopyBwPageEquivalentCount: 0,
                dayCopyColorPageEquivalentCount: 0,
                dayCopyPageCount: 0,
                dayCopyPageBwCount: 0,
                dayCopyPageColorCount: 0,
                dayScanPageCount: 0,
                total: 0
            },
            loadChart: true,
            chartInitialized : false,
            emptyRangeLabel: {},
            emptyPrintChart: false,
            emptyResourcesChart: false,
            height: 400,
            width: 400,
            chartData: [],
            d: [],
            p: [],
            diffDateChart: 0
        }
    },
    directives: {
        resize,
    },
    computed: {
        appUser() {
            return this.$store.getters.user;
        },
        aclUser() {
            return new AclHandler(this.appUser, this.isCloudMode);
        },
        isDefaultAdmin() {
            return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] }) && this.aclUser.hasDefaultLocation();
        },
        canEditDevice() {
            return this.aclUser.hasGrantForOperation('BTN_EDIT_DEVICE', 'EXECUTE') || false;
        },
        isDownloadWalkFile() {
            const hasGrant = this.aclUser.hasGrantForOperation('BTN_DOWNLOAD_WALK', 'EXECUTE') || false;

            return this.isCloudMode ? hasGrant && this.isDefaultAdmin : hasGrant;
        },
        isUpdateWalkFile() {
            const hasGrant = this.aclUser.hasGrantForOperation('BTN_DOWNLOAD_WALK', 'EXECUTE') || false;

            return this.isCloudMode ? hasGrant && this.isDefaultAdmin : hasGrant;
        },
        isNotAvailable() {
            return this.deviceDays && this.deviceDays.device.status && this.deviceDays.device.status.code === 'NOT_AVAILABLE';
        },
        dim02Code() {
            return this.deviceDays.device.model.dim02 && this.deviceDays.device.model.dim02.code;
        },
        dim03Code() {
            return this.deviceDays.device.model.dim03 && this.deviceDays.device.model.dim03.code;
        },
        // Таблица Счетчики
        counters() {
            if (!this.perPeriodTypesCounters || !this.devices)
                return [];

            const days = this.devices.length + this.diffDateChart;

            const avgDaily = (value) => {
                const avg = value / days;
                return Number.isFinite(avg) ? avg.toFixed(1) : 0;
            };

            const countersMap = [
                { totalKey: 'lixPrintedPageCount', periodKey: 'total', labelKey: 'device.detail.printed' },
                { totalKey: 'lixPrintedBwPageCount', periodKey: 'dayPrintedBwPageCount', labelKey: 'device.detail.bw' },
                { totalKey: 'lixPrintedColorPageCount', periodKey: 'dayPrintedColorPageCount', labelKey: 'device.detail.color' },
                { totalKey: 'lixPrintedPageA4Count', periodKey: 'dayPrintedPageA4Count', labelKey: 'device.detail.a4' },
                { totalKey: 'lixPrintedPageA4BwCount', periodKey: 'dayPrintedBwPageA4Count', labelKey: 'device.detail.bwA4' },
                { totalKey: 'lixPrintedPageA4ColorCount', periodKey: 'dayPrintedColorPageA4Count', labelKey: 'device.detail.colorA4' },
                { totalKey: 'lixPrintedPageA3Count', periodKey: 'dayPrintedPageA3Count', labelKey: 'device.detail.a3' },
                { totalKey: 'lixPrintedPageA3BwCount', periodKey: 'dayPrintedBwPageA3Count', labelKey: 'device.detail.bwA3' },
                { totalKey: 'lixPrintedPageA3ColorCount', periodKey: 'dayPrintedColorPageA3Count', labelKey: 'device.detail.colorA3' },
                { totalKey: 'lixPrintedBwPageEquivalentCount', periodKey: 'dayPrintedBwPageEquivalentCount', labelKey: 'device.detail.bwEquivalent' },
                { totalKey: 'lixPrintedColorPageEquivalentCount', periodKey: 'dayPrintedColorPageEquivalentCount', labelKey: 'device.detail.colorEquivalent' },
                { totalKey: 'lixCopyBwPageEquivalentCount', periodKey: 'dayCopyBwPageEquivalentCount', labelKey: 'device.detail.copyBwEquivalent' },
                { totalKey: 'lixCopyColorPageEquivalentCount', periodKey: 'dayCopyColorPageEquivalentCount', labelKey: 'device.detail.copyColorEquivalent' },
                { totalKey: 'lixScanPageCount', periodKey: 'dayScanPageCount', labelKey: 'device.detail.scans' },
                { totalKey: 'lixCopyPageCount', periodKey: 'dayCopyPageCount', labelKey: 'device.detail.copies' },
                { totalKey: 'lixCopyPageBwCount', periodKey: 'dayCopyPageBwCount', labelKey: 'device.detail.bwCopies' },
                { totalKey: 'lixCopyPageColorCount', periodKey: 'dayCopyPageColorCount', labelKey: 'device.detail.colorCopies' }
            ];

            let data = countersMap.map(({ totalKey, periodKey, labelKey }) => {
            const total = this.deviceDays[totalKey];
            const period = this.perPeriodTypesCounters[periodKey];
                return {
                    name: this.$t(labelKey),
                    total,
                    period,
                    averageDaily: avgDaily(period)
                };
            }).filter(el => el.total !== null && el.total !== undefined);

            const excludeNames = new Set();

            if (this.dim02Code === 'BW')
                excludeNames.add(this.$t('device.detail.bw'));

            if (this.dim03Code === 'A4')
                excludeNames.add(this.$t('device.detail.a4'));

            if (this.dim02Code === 'BW' && this.dim03Code === 'A4')
                excludeNames.add(this.$t('device.detail.bwA4'));

            if (this.dim02Code === 'COLOR' && this.dim03Code === 'A4') {
                excludeNames.add(this.$t('device.detail.colorA4'));
                excludeNames.add(this.$t('device.detail.bwA4'));
            }

            data = data.filter(el => !excludeNames.has(el.name));

            return data;
        }
    },
    methods: {
        getFillStyle, getClassProgress, color,
        onResize(e) {
            this.width = e.offsetWidth - 12;
        },
        formatDateLegend(date) {
            return moment(date).format('DD.MM.YYYY');
        },
        classBtnActions() {
            const nameClass = 'device-detail-buttons';
            return `${nameClass} ${this.width <= 680 ? 'low' : 'high'}`;
        },
        customizeLabelText({ value }) {
            return moment(value).format('DD.MM.YYYY');
        },
        async onSave() {
            try {
                this.saving = true;
                this.saveDisabled = true;
                if (!this.id)
                    return;

                await putDevice(this.id, this.deviceDays.device);
                notifier.showSuccess(this.$t('general.updateSuccess'));
            } catch(err) {
                this.onDataError(err);
            } finally {
                this.saving = false;
                this.saveDisabled = false;
            }
        },
        async onDelete() {
            const response = await showConfirmDialog({
                title: this.$t('general.delete'),
                message: this.$t('device.detail.messages.deleteDevice'),
                btnType: 'default',
                okButton: this.$t('general.proceed'),
                cancelButton: this.$t('general.cancel'),
            });

            if (!response)
                return;

            try {
                this.deleting = true;
                await deleteDevice(this.id);
                notifier.showSuccess(this.$t('general.deleteSuccess'));
                setTimeout(() => this.$router.go(-1));
            } catch(err) {
                this.onDataError(err);
            } finally {
                this.deleting = false;
            }
        },
        async onDownloadWalkFile() {
            this.disableDownloadWalkFileButton = true;
            try {
                await download(`/dm-device-days-v/device/${this.id}/walk/unknown_device.zip`, `${this.deviceDays.pixIp}-${this.deviceDays.device.modelCode}.zip`);
                notifier.showSuccess(this.$t('general.uploadSuccess'));
            } catch (err) {
                this.onDataError(err);
            }
            this.disableDownloadWalkFileButton = false;
        },
        async onUpdateWalkFile() {
            try {
                const res = await updateWalkFile(this.id);
                notifier.showSuccess(res.message);
            } catch (err) {
                this.onDataError(err);
            }
        },
        updatePeriod() {
            let filter = { ...this.dataFilter };
            if (this.dataFilter.periodType !== 'FREE_CHOICE') {
                delete filter.from;
                delete filter.to;
            }
            this.$store.commit("setDeviceCardInfPeriod", filter);
        },
        changeFrom(e) {
            this.updatePeriod();
            this.changePeriodTypeFrom(e);
        },
        changeTo(e) {
            this.updatePeriod();
            this.changePeriodTypeTo(e);
        },
        async getChart() {
            try {
                this.loadChart = true;
                this.diffDateChart = 0;

                // Обнуляем все счётчики
                const counters = Object.keys(this.perPeriodTypesCounters);
                counters.forEach(el => {
                    this.perPeriodTypesCounters[el] = 0;
                });

                const device = await findDeviceDays({
                    filterJson: [
                            ["factDate",">=",this.dataFilter.from],
                            "and",
                            ["factDate","<=",this.dataFilter.to],
                        "and",
                        ["device.id","=",this.id]
                    ],
                    page: '900,1',
                    sort: ['factDate,ASC']
                });

                // Обработка строк
                this.devices = device.pageRows.map(row => {
                    const formatted = { factDate: moment(row.factDate).format('YYYY-MM-DD') };

                    counters.forEach(key => {
                        formatted[key] = row[key] != null ? row[key] : 0;
                        if (key !== 'total') {
                            this.perPeriodTypesCounters[key] += formatted[key];
                        }
                    });

                    this.perPeriodTypesCounters.total += formatted.dayPrintedBwPageCount + formatted.dayPrintedColorPageCount;

                    return formatted;
                });

                // Проверка на пустой график печати
                this.emptyPrintChart = !this.devices.some(el => el.dayPrintedBwPageCount || el.dayPrintedColorPageCount);

                // Проверка разницы между from и первой датой в данных
                const formattedFrom = moment(this.dataFilter.from).format('YYYY-MM-DD');
                const startDate = this.devices.length > 0 ? this.devices[0].factDate : formattedFrom;

                if (moment(startDate).diff(moment(formattedFrom), 'days') !== 0) {
                    this.devices.unshift({ factDate: formattedFrom });
                    this.diffDateChart = moment(startDate).diff(moment(formattedFrom), 'days');
                }

                this.chartData = await get(`/dm-device-part-days-v/${this.id}/resources?periodFrom=${this.dataFilter.from}&periodTo=${this.dataFilter.to}`);

                this.p = [];
                const tempParts = {};

                this.d = Object.keys(this.chartData).map(dateKey => {
                    const resourceLevels = {};

                    this.chartData[dateKey].forEach(part => {
                        const partKey = `${part.partCode}${part.partId}`;

                        resourceLevels[partKey] = part.level;
                        resourceLevels[part.partCode] = part.level;

                        tempParts[part.partId] = part;
                    });

                    resourceLevels.factDate = moment(dateKey).format('YYYY-MM-DD');
                    return resourceLevels;
                });

                // Уникальные ресурсы, отсортированные по типу
                this.p = sortBy(Object.values(tempParts), part => {
                    return deviceResourcesTypes.indexOf(part.partCode);
                });

                // Если начальная дата отличается, добавим null'ы в первый объект devices
                if (startDate !== this.dataFilter.from && this.d.length > 0) {
                    const firstPoint = { ...this.d[0] };
                    Object.keys(firstPoint).forEach(code => {
                        if (code !== 'factDate') {
                            this.devices[0][code] = null;
                        }
                    });
                }

                // Объединяем ресурсы по дате
                this.d.forEach(resourceData => {
                    const index = findIndex(this.devices, { factDate: resourceData.factDate });
                    if (index !== -1) {
                        this.devices[index] = { ...this.devices[index], ...resourceData };
                    } else {
                        this.devices.push({ ...resourceData });
                    }
                });

                // Сортируем устройства по дате, чтобы всё было по порядку
                this.devices.sort((a, b) => new Date(a.factDate) - new Date(b.factDate));

                // Проверка на пустой график РМ
                this.emptyResourcesChart = this.p.length === 0;

                this.loadChart = false;
            } catch (err) {
                this.onDataError(err);
                this.loadChart = false;
            }
        },
        async loadData() {
            try {
                // Таблица Расходные материалы
                const deviceResources = await findDeviceResources({
                    filterJson: [
                            ["factDate","=",moment.utc().format('YYYY-MM-DD')],
                            "and",
                            ["devicePart.device.id","=",this.id]
                    ],
                    page: '900,1',
                    sort: ['devicePart.part.partType.id,ASC']
                });

                // Сортировка по индексу кода PartType в deviceResourcesTypes
                const sortedResources = sortBy(deviceResources.pageRows, item => {
                    const code = item.devicePart.part.partType.code;
                    return deviceResourcesTypes.indexOf(code);
                });
                this.resources = sortedResources
                    .filter(el => el.devicePart.removeDatetime === null)
                    .map(el =>  ({
                        name: el.devicePart.part.partType.name,
                        percentLevel: el.level <= 4 ? 4 : el.level,
                        level: `${el.level}%`,
                        icon: el.devicePart.part.partType.code
                    }))
                    .filter(el => el.percentLevel || el.percentLevel === 0);

                // Диаграмма Объемы печати и Диаграмма Расходные материалы - история
                await this.getChart();

                this.chartInitialized = true;
            } catch(err) {
                this.onDataError(err);
            }
        },
        handleResize() {
            this.height = window.innerHeight - PAGE_WHITESPACE;
        },
    },
    async created() {
        window.addEventListener('resize', this.handleResize);

        try {
            this.periodTypeDict = await this.getDictionaryItems('RPT_PERIOD_TYPE');

            if (this.$store.getters.deviceCardInfPeriod)
                this.dataFilter = {...this.dataFilter, ...this.$store.getters.deviceCardInfPeriod};
        } catch(err) {
            this.onDataError(err);
        }

        this.loadData();
    },
    mounted() {
        this.height = window.innerHeight - PAGE_WHITESPACE;
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    watch: {
        id() {
            this.loadData();
        },
        emptyPrintChart(val) {
            this.emptyRangeLabel = val ? { startValue: 0, endValue: 100 } : {};
        },
        dataFilter: {
            handler() {
                if (this.chartInitialized) {
                    this.getChart();
                }
            },
            deep: true
        }
    },
}
</script>

<style lang="scss">
.card-device {
    display: flex;
    flex-direction: row;
    column-gap: 24px;
    width: 100%;
    overflow: hidden;

    .resource-counter {
        flex: 0 0 410px;

        .detail {
            display: flex;
            flex-direction: row;
            column-gap: 15px;
        }

        .grid {
            border-radius: 4px;
            padding: 5px;
            margin: 0;
            margin-top: 10px;

            border: 1px solid #ddd;

            legend {
                font-weight: 500;
            }

            .heading {
                display: flex;
                flex-direction: row;
                justify-content: space-between;

                .caption {
                    flex: 1;
                }

                .datagrid {
                    .headers {
                        padding: 0 7px;
                    }

                    .headers {
                        display: inline-flex;
                        justify-content: flex-end;
                        color: #959595;
                        font-weight: 400;
                    }
                }
            }

            .dx-datagrid-headers,
            .dx-datagrid-headers .dx-datagrid-table .dx-row > td,
            .dx-datagrid-borders .dx-datagrid-rowsview, .dx-datagrid-headers + .dx-datagrid-rowsview, .dx-datagrid-rowsview.dx-datagrid-after-headers {
                border: none;
            }

            .dx-progressbar-container {
                height: 10px;
            }
        }
    }
    
    .print-volumes {
        flex: 1 1;
        overflow: hidden;

        .period-type {
            display: flex;
            flex-direction: row;
            align-items: center;
            column-gap: 10px;
            margin-bottom: 10px;

            .dx-item {
                margin-bottom: 0px;
            }

            .dx-layout-manager .dx-field-item:not(.dx-last-col) {
                padding-right: 0px;
            }
        }

        .device-print {
            position: relative;
            width: 100%;
            padding: 5px;
            margin: 0;
            margin-bottom: 10px;
            border-radius: 4px;
            border: 1px solid #ddd;

            legend {
                font-weight: 500;
            }
        }

        .comment {
            display: flex;
            align-items: center;
            flex-direction: row;
        }
    }
}

.my-legend {
    .my-legend-title {
        white-space: pre;
        font-size: 18px;
        font-weight: 200;
        margin: 0.5em;
        text-align: center;
    }

    tr {
        display: flex;
        justify-content: space-between;
    }

    .print-volumes-bw {
        padding-top: 8px;
        margin-top: 8px;
        border-top: 1px solid #ccc;
    }

    .my-legend-serie {
        margin: 0 10px 0 20px;
        position: relative;
        color: attr(data-serie-color);

        &:before {
            content: "";
            display: block;
            width: 12px;
            height: 12px;
            position: absolute;
            left: -24px;
            top: 0;
            background-color: var(--serie-color);
            border: thin dotted black;
        }
    }
    .my-legend-value {
        text-align: right;
    }
}

.emptyResourcesChart {
    top: 0;
    right: 0;
}

.emptyPrintChart {
    bottom: 25px;
    right: 0;
}

.emptyChart {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    position: absolute;

    .text {
        color: #999;
        font-size: 17px;
    }
}

.device-detail-buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    column-gap: 7px;
    margin-top: 10px;
}

.device-detail-buttons.low {
    .dx-button {
        padding: 0 12px;
    }
}

.device-detail-buttons.high {
    .dx-button {
        width: 200px;
    }
}
</style>